import React, { useState } from 'react';
import styled from 'styled-components';
import { FaUpload, FaLink } from 'react-icons/fa';

// This component handles both file uploads and URL input for images
const ImageUploader = ({ onImageUpload }) => {
  const [isUsingUrl, setIsUsingUrl] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }
    
    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }
    
    setError('');
    setIsUploading(true);
    
    // In a real implementation, you would upload the image to a server
    // Here we'll simulate the upload and use a local URL
    const reader = new FileReader();
    reader.onload = (e) => {
      // Simulate API delay
      setTimeout(() => {
        onImageUpload(e.target.result);
        setIsUploading(false);
      }, 1000);
    };
    reader.readAsDataURL(file);
  };
  
  const handleUrlSubmit = (e) => {
    e.preventDefault();
    if (!imageUrl) {
      setError('Please enter an image URL');
      return;
    }
    
    // Simple URL validation
    if (!imageUrl.match(/^(http|https):\/\/[^ "]+$/)) {
      setError('Please enter a valid URL');
      return;
    }
    
    setError('');
    onImageUpload(imageUrl);
  };
  
  return (
    <Container>
      <OptionToggle>
        <ToggleButton 
          type="button" 
          $active={!isUsingUrl} 
          onClick={() => setIsUsingUrl(false)}
        >
          <FaUpload /> Upload File
        </ToggleButton>
        <ToggleButton 
          type="button" 
          $active={isUsingUrl} 
          onClick={() => setIsUsingUrl(true)}
        >
          <FaLink /> Use URL
        </ToggleButton>
      </OptionToggle>
      
      {isUsingUrl ? (
        <UrlForm onSubmit={handleUrlSubmit}>
          <UrlInput 
            type="text" 
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Enter image URL"
          />
          <SubmitButton type="submit">Use Image</SubmitButton>
        </UrlForm>
      ) : (
        <FileUploadContainer>
          <FileInputLabel $isUploading={isUploading}>
            {isUploading ? 'Uploading...' : 'Choose Image'}
            <FileInput 
              type="file" 
              accept="image/*"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </FileInputLabel>
          <HelpText>Supported formats: JPG, PNG, GIF. Max size: 2MB</HelpText>
        </FileUploadContainer>
      )}
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

const Container = styled.div`
  margin: 10px 0;
`;

const OptionToggle = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: ${props => props.$active ? 'rgba(100, 255, 218, 0.1)' : 'transparent'};
  color: ${props => props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:first-child {
    border-radius: 4px 0 0 4px;
  }
  
  &:last-child {
    border-radius: 0 4px 4px 0;
  }
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.05);
  }
`;

const FileUploadContainer = styled.div`
  text-align: center;
`;

const FileInputLabel = styled.label`
  display: block;
  width: 100%;
  padding: 12px;
  border: 2px dashed ${props => props.$isUploading ? 'var(--accent)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 4px;
  color: ${props => props.$isUploading ? 'var(--accent)' : 'var(--text-primary)'};
  text-align: center;
  cursor: ${props => props.$isUploading ? 'wait' : 'pointer'};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.$isUploading ? 'var(--accent)' : 'rgba(255, 255, 255, 0.4)'};
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const HelpText = styled.p`
  margin: 8px 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const UrlForm = styled.form`
  display: flex;
  gap: 10px;
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const SubmitButton = styled.button`
  padding: 10px 16px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  font-size: 0.9rem;
  margin-top: 8px;
`;

export default ImageUploader;